import React, { useState, useEffect, useCallback } from 'react';
import {
    X, CheckSquare, Award, BookOpen, AlertTriangle,
    XCircle, Download, RefreshCw, BarChart3, TrendingUp,
    Users, AlertCircle, CheckCircle, ChevronRight, Briefcase, UserPlus, Clock, Activity,
    Maximize2, Minimize2, Bell, Zap, Send
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
    StatCard,
    ProgressRing,
    BarChart,
    DonutChart,
    FilterBar,
    DataTable,
    EmployeeDetailModal
} from './index';
import useDrawerResize from './useDrawerResize';
import AnalyticsAIPanel from './AnalyticsAIPanel';
import NotificationSettingsPanel from './NotificationSettingsPanel';
import ReminderHistory from './ReminderHistory';

const EmployeesAnalyticsDrawer = ({ isOpen, onClose, employeeFilter = null }) => {
    // State
    const [activeTab, setActiveTab] = useState('employees');
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        dateRange: '30',
        teamId: 'all',
        department: 'all',
        position: 'all',
        riskLevel: 'all'
    });
    const [reminderSummary, setReminderSummary] = useState(null);

    // ── Resizable drawer ──
    const { drawerWidth, isDragging, dragHandleProps, dragHandleElement, isFullscreen, toggleFullscreen } = useDrawerResize({
        minWidth: 400, maxWidth: 800, defaultWidth: 600,
        storageKey: 'employeeAnalyticsDrawerWidth',
    });
    const [filterOptions, setFilterOptions] = useState({
        teams: [],
        departments: [],
        positions: []
    });

    // Data state
    const [preboardingData, setPreboardingData] = useState(null);
    const [onboardingData, setOnboardingData] = useState(null);
    const [employeeData, setEmployeeData] = useState(null);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

    // Fetch filter options
    const fetchFilterOptions = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.get('/analytics/filters', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setFilterOptions(response.data.filters);
        } catch (error) {
            console.error('Error fetching filter options:', error);
        }
    };

    const fetchReminderSummary = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await api.get('/reminders/summary', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setReminderSummary(res.data.summary);
        } catch (error) {
            console.error('Error fetching reminder summary:', error);
        }
    };

    const handleDownloadCSV = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.post('/analytics/report', {
                reportType: 'employees', format: 'csv'
            }, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'employee-analytics.csv';
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('Employee analytics exported');
        } catch { toast.error('Export failed'); }
    };

    // Individual employee detail state
    const [individualData, setIndividualData] = useState(null);

    // Fetch data based on active tab
    const fetchData = useCallback(async () => {
        if (!isOpen) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            // If filtering to a single employee, use the individual endpoint
            if (employeeFilter?.id) {
                const [detailRes, trendsRes] = await Promise.all([
                    api.get(`/analytics/employees/${employeeFilter.id}/details`, { headers }),
                    api.get(`/analytics/employees/${employeeFilter.id}/trends`, { headers }).catch(() => ({ data: null })),
                ]);
                setIndividualData({
                    details: detailRes.data,
                    trends: trendsRes.data,
                });
                setLoading(false);
                return;
            }

            // Build query params
            const params = new URLSearchParams();
            if (filters.dateRange !== 'all') params.append('period', filters.dateRange);
            if (filters.teamId !== 'all') params.append('teamId', filters.teamId);
            if (filters.department !== 'all') params.append('department', filters.department);
            if (filters.riskLevel !== 'all') params.append('riskLevel', filters.riskLevel);

            const queryString = params.toString();
            const requests = [];

            // Determine which endpoints to call based on active tab
            if (activeTab === 'preboarding') {
                requests.push(api.get(`/analytics/preboarding${queryString ? '?' + queryString : ''}`, { headers }));
            } else if (activeTab === 'onboarding') {
                requests.push(api.get(`/analytics/onboarding${queryString ? '?' + queryString : ''}`, { headers }));
            } else if (activeTab === 'employees') {
                requests.push(api.get(`/analytics/employees${queryString ? '?' + queryString : ''}`, { headers }));
            }

            const responses = await Promise.all(requests);

            if (activeTab === 'preboarding') {
                setPreboardingData(responses[0].data.analytics);
            } else if (activeTab === 'onboarding') {
                setOnboardingData(responses[0].data.analytics);
            } else if (activeTab === 'employees') {
                setEmployeeData(responses[0].data);
            }

        } catch (error) {
            console.error('Error fetching analytics:', error);
            toast.error('Failed to load analytics data');
        } finally {
            setLoading(false);
        }
    }, [isOpen, activeTab, filters, employeeFilter]);

    useEffect(() => {
        if (isOpen) {
            fetchFilterOptions();
            fetchData();
            fetchReminderSummary();
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
            setPreboardingData(null);
            setOnboardingData(null);
            setEmployeeData(null);
            setIndividualData(null);
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, fetchData]);

    // Close on Escape
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    // Format time helper
    const formatTime = (minutes) => {
        if (!minutes) return '-';
        if (minutes < 60) return `${Math.round(minutes)}m`;
        const hours = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        return `${hours}h ${mins}m`;
    };

    // Reset filters
    const handleResetFilters = () => {
        setFilters({
            dateRange: '30',
            teamId: 'all',
            department: 'all',
            position: 'all',
            riskLevel: 'all'
        });
    };

    const tabs = [
        { id: 'employees', label: 'Employees', icon: Users },
        { id: 'onboarding', label: 'Onboarding', icon: TrendingUp },
        { id: 'preboarding', label: 'Preboarding', icon: UserPlus }
    ];

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end transform transition-transform duration-300">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Drawer */}
            <div
                className="relative h-full bg-[#FAFAF9] shadow-2xl flex flex-col"
                style={{
                    width: isFullscreen ? '100vw' : `${drawerWidth}px`,
                    maxWidth: '100vw',
                    transition: isDragging ? 'none' : 'width 0.3s ease',
                }}
            >
                {/* ── Drag handle (left edge) — hidden in fullscreen ── */}
                {!isFullscreen && (
                    <div {...dragHandleProps}>
                        <div {...dragHandleElement} />
                    </div>
                )}

                {/* Header */}
                <div className="px-6 py-4 bg-white border-b border-[#E5E1DD] flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 rounded-lg">
                            <BarChart3 className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">
                                {employeeFilter ? `${employeeFilter.first_name || ''} ${employeeFilter.last_name || ''}`.trim() + ' — Analytics' : 'Employee Analytics'}
                            </h2>
                            <p className="text-sm text-gray-500">
                                {employeeFilter ? `Individual analytics for ${employeeFilter.email || 'this employee'}` : 'Insights on onboarding, preboarding, and employee performance'}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleDownloadCSV}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Download CSV"
                        >
                            <Download className="w-5 h-5" />
                        </button>
                        <button
                            onClick={fetchData}
                            disabled={loading}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Refresh Data"
                        >
                            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                            onClick={toggleFullscreen}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                        >
                            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Individual Employee View — when filtered to a single employee */}
                {employeeFilter && individualData && (
                    <div className="flex-1 overflow-y-auto p-6 min-w-0 space-y-6">
                        {/* Employee Info Header */}
                        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-lg">
                                {(employeeFilter.first_name?.[0] || '').toUpperCase()}{(employeeFilter.last_name?.[0] || '').toUpperCase()}
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">
                                    {employeeFilter.first_name} {employeeFilter.last_name}
                                </h3>
                                <p className="text-sm text-gray-500">{employeeFilter.position || employeeFilter.email}</p>
                                <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${employeeFilter.lifecycle_stage === 'active' ? 'bg-green-100 text-green-700' :
                                    employeeFilter.lifecycle_stage === 'onboarding' ? 'bg-blue-100 text-blue-700' :
                                        'bg-amber-100 text-amber-700'
                                    }`}>
                                    {employeeFilter.lifecycle_stage?.charAt(0).toUpperCase() + employeeFilter.lifecycle_stage?.slice(1)}
                                </span>
                            </div>
                        </div>

                        {/* Key Metrics */}
                        {individualData.details?.stats && (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                <StatCard
                                    title="Total Tasks"
                                    value={individualData.details.stats.totalTasks || 0}
                                    subtitle={`${individualData.details.stats.completedTasks || 0} completed`}
                                    icon={CheckSquare}
                                    color="blue"
                                />
                                <StatCard
                                    title="Completion Rate"
                                    value={`${individualData.details.stats.completionRate || 0}%`}
                                    subtitle={individualData.details.stats.completionRate >= 80 ? 'On track' : 'Needs attention'}
                                    icon={TrendingUp}
                                    color={individualData.details.stats.completionRate >= 80 ? 'green' : 'red'}
                                />
                                <StatCard
                                    title="Avg Score"
                                    value={`${individualData.details.stats.avgScore || 0}%`}
                                    subtitle="Quiz average"
                                    icon={Award}
                                    color="blue"
                                />
                                <StatCard
                                    title="Overdue"
                                    value={individualData.details.stats.overdueTasks || 0}
                                    subtitle="Tasks past due"
                                    icon={AlertTriangle}
                                    color={individualData.details.stats.overdueTasks > 0 ? 'red' : 'gray'}
                                />
                            </div>
                        )}

                        {/* Task List */}
                        {individualData.details?.tasks && individualData.details.tasks.length > 0 && (
                            <div className="bg-white rounded-xl border border-gray-200 p-5">
                                <h3 className="font-semibold text-gray-800 mb-4">Task Assignments</h3>
                                <DataTable
                                    data={individualData.details.tasks}
                                    columns={[
                                        { key: 'title', label: 'Task', render: (val) => <span className="font-medium text-gray-900">{val}</span> },
                                        {
                                            key: 'task_type', label: 'Type', render: (val) => (
                                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                                    {val}
                                                </span>
                                            )
                                        },
                                        {
                                            key: 'status', label: 'Status', render: (val) => (
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${val === 'completed' ? 'bg-green-100 text-green-700' :
                                                    val === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                                        val === 'overdue' ? 'bg-red-100 text-red-700' :
                                                            'bg-gray-100 text-gray-700'
                                                    }`}>{val?.replace('_', ' ')}</span>
                                            )
                                        },
                                        { key: 'score', label: 'Score', render: (val) => val ? `${val}%` : '-' },
                                    ]}
                                    searchable
                                    searchKeys={['title']}
                                    pageSize={10}
                                />
                            </div>
                        )}

                        {/* AI Insights for this employee */}
                        <AnalyticsAIPanel
                            analyticsContext={{
                                type: 'individual-employee',
                                id: employeeFilter.id,
                                title: `${employeeFilter.first_name} ${employeeFilter.last_name}`,
                                data: individualData,
                            }}
                        />
                    </div>
                )}

                {/* Standard multi-employee view: Tabs, Filters, Content */}
                {!employeeFilter && (
                    <>
                        {/* Tabs */}
                        <div className="bg-card border-b border-border px-6">
                            <div className="flex space-x-8 -mb-px">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`
                                        group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors
                                        ${activeTab === tab.id
                                                ? 'border-primary text-primary'
                                                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'}
                                    `}
                                    >
                                        <tab.icon className={`
                                        -ml-0.5 mr-2 h-5 w-5
                                        ${activeTab === tab.id ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}
                                    `} />
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Filters */}
                        <div className="bg-card px-6 py-4 border-b border-border">
                            <FilterBar
                                filters={filters}
                                options={{
                                    teams: filterOptions.teams,
                                    departments: filterOptions.departments,
                                    positions: filterOptions.positions,
                                    showDateRange: true,
                                    showRiskLevel: activeTab === 'employees'
                                }}
                                onChange={setFilters}
                                onReset={handleResetFilters}
                                onRefresh={fetchData}
                                loading={loading}
                            />
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-6 scrollbar-hide min-w-0">
                            {loading && !preboardingData && !onboardingData && !employeeData ? (
                                <div className="flex items-center justify-center h-64">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {/* Preboarding Tab */}
                                    {activeTab === 'preboarding' && preboardingData && (
                                        <div className="space-y-6">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <StatCard
                                                    title="Preboarding Employees"
                                                    value={preboardingData.summary?.totalPreboardingEmployees || 0}
                                                    subtitle="Currently in preboarding"
                                                    icon={UserPlus}
                                                    color="blue"
                                                />
                                                <StatCard
                                                    title="Task Completion Rate"
                                                    value={`${preboardingData.summary?.completionRate || 0}%`}
                                                    subtitle={`${preboardingData.summary?.completedTasks || 0} of ${preboardingData.summary?.totalTasks || 0} tasks`}
                                                    icon={CheckSquare}
                                                    color="blue"
                                                />
                                            </div>

                                            {/* Compliance Progress */}
                                            <div className="bg-card rounded-xl border border-border p-6">
                                                <div className="flex items-center justify-between mb-4">
                                                    <div>
                                                        <h3 className="font-semibold text-foreground">Preboarding Compliance</h3>
                                                        <p className="text-sm text-muted-foreground">Compliance task completion for preboarding employees</p>
                                                    </div>
                                                    <ProgressRing
                                                        value={preboardingData.compliance?.completionRate || 0}
                                                        size={80}
                                                        label="Complete"
                                                    />
                                                </div>
                                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-border">
                                                    <div className="text-center">
                                                        <p className="text-2xl font-bold text-foreground">{preboardingData.compliance?.totalTasks || 0}</p>
                                                        <p className="text-sm text-muted-foreground">Total Tasks</p>
                                                    </div>
                                                    <div className="text-center">
                                                        <p className="text-2xl font-bold text-blue-600">{preboardingData.compliance?.completed || 0}</p>
                                                        <p className="text-sm text-muted-foreground">Completed</p>
                                                    </div>
                                                    <div className="text-center">
                                                        <p className="text-2xl font-bold text-red-600">{preboardingData.compliance?.overdue || 0}</p>
                                                        <p className="text-sm text-muted-foreground">Overdue</p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Completion by Group */}
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                                {/* By Team */}
                                                <div className="bg-card rounded-xl border border-border p-6">
                                                    <h3 className="font-semibold text-foreground mb-4">Completion by Team</h3>
                                                    {preboardingData.byTeam && preboardingData.byTeam.length > 0 ? (
                                                        <BarChart
                                                            data={preboardingData.byTeam.slice(0, 5).map(t => ({
                                                                label: t.team_name,
                                                                value: t.completion_rate,
                                                                suffix: '%'
                                                            }))}
                                                            horizontal
                                                            barColor="#60A5FA"
                                                        />
                                                    ) : (
                                                        <p className="text-muted-foreground text-sm">No team data available</p>
                                                    )}
                                                </div>

                                                {/* By Department */}
                                                <div className="bg-card rounded-xl border border-border p-6">
                                                    <h3 className="font-semibold text-foreground mb-4">Completion by Department</h3>
                                                    {preboardingData.byDepartment && preboardingData.byDepartment.length > 0 ? (
                                                        <BarChart
                                                            data={preboardingData.byDepartment.slice(0, 5).map(d => ({
                                                                label: d.department,
                                                                value: d.completion_rate,
                                                                suffix: '%'
                                                            }))}
                                                            horizontal
                                                            barColor="#3B82F6"
                                                        />
                                                    ) : (
                                                        <p className="text-muted-foreground text-sm">No department data available</p>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Preboarding Employee Progress Table */}
                                            <div className="bg-card rounded-xl border border-border overflow-hidden">
                                                <div className="p-4 border-b border-border">
                                                    <h3 className="font-semibold text-foreground">Preboarding Progress</h3>
                                                </div>
                                                {preboardingData.employeeProgress && preboardingData.employeeProgress.length > 0 ? (
                                                    <DataTable
                                                        data={preboardingData.employeeProgress}
                                                        columns={[
                                                            { key: 'name', label: 'Employee', render: (val) => <span className="font-medium text-foreground">{val}</span> },
                                                            { key: 'department', label: 'Dept' },
                                                            {
                                                                key: 'completionRate', label: 'Progress', render: (val, row) => (
                                                                    <div className="flex flex-col gap-1 w-24">
                                                                        <div className="flex justify-between text-xs text-muted-foreground">
                                                                            <span>{val}%</span>
                                                                        </div>
                                                                        <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                                                                            <div className="h-full bg-amber-500 rounded-full" style={{ width: `${val}%` }} />
                                                                        </div>
                                                                    </div>
                                                                )
                                                            },
                                                            {
                                                                key: 'overdueTasks', label: 'Overdue', render: (val) => (
                                                                    <span className={val > 0 ? 'text-red-600 font-semibold' : 'text-muted-foreground'}>
                                                                        {val}
                                                                    </span>
                                                                )
                                                            }
                                                        ]}
                                                        searchable
                                                        searchKeys={['name']}
                                                        pageSize={5}
                                                    />
                                                ) : (
                                                    <div className="p-8 text-center text-muted-foreground">
                                                        <p>No employees currently in preboarding</p>
                                                    </div>
                                                )}
                                            </div>

                                            {/* All Employees with Preboarding History */}
                                            <div className="bg-card rounded-xl border border-border overflow-hidden">
                                                <div className="p-4 border-b border-border">
                                                    <h3 className="font-semibold text-foreground">Preboarding History</h3>
                                                    <p className="text-sm text-muted-foreground">All employees who have completed or are completing preboarding tasks</p>
                                                </div>
                                                {preboardingData.allEmployeesWithPreboarding && preboardingData.allEmployeesWithPreboarding.length > 0 ? (
                                                    <DataTable
                                                        data={preboardingData.allEmployeesWithPreboarding}
                                                        columns={[
                                                            { key: 'name', label: 'Employee', render: (val) => <span className="font-medium text-foreground">{val}</span> },
                                                            {
                                                                key: 'status', label: 'Status', render: (val) => (
                                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${val === 'preboarding' ? 'bg-blue-50 text-blue-700' :
                                                                        val === 'onboarding' ? 'bg-blue-100 text-blue-700' :
                                                                            val === 'active' ? 'bg-blue-100 text-blue-700' :
                                                                                'bg-muted text-muted-foreground'
                                                                        }`}>{val}</span>
                                                                )
                                                            },
                                                            { key: 'department', label: 'Department' },
                                                            {
                                                                key: 'preboardingProgress', label: 'Preboarding Progress', render: (val, row) => (
                                                                    <div className="flex flex-col gap-1 w-28">
                                                                        <div className="flex justify-between text-xs text-muted-foreground">
                                                                            <span>{val}%</span>
                                                                            <span>{row.completedPreboardingTasks}/{row.totalPreboardingTasks}</span>
                                                                        </div>
                                                                        <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                                                                            <div className={`h-full rounded-full ${val === 100 ? 'bg-green-500' : 'bg-amber-500'}`} style={{ width: `${val}%` }} />
                                                                        </div>
                                                                    </div>
                                                                )
                                                            },
                                                            { key: 'daysToComplete', label: 'Days to Complete', render: (val) => val ? `${val} days` : 'In Progress' }
                                                        ]}
                                                        searchable
                                                        searchKeys={['name', 'department', 'status']}
                                                        pageSize={5}
                                                    />
                                                ) : (
                                                    <div className="p-8 text-center text-gray-500">
                                                        <p>No preboarding data available</p>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Bottleneck Tasks */}
                                            {preboardingData.bottlenecks && preboardingData.bottlenecks.length > 0 && (
                                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                                                    <div className="flex items-start gap-3">
                                                        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                                        <div className="flex-1">
                                                            <h4 className="font-semibold text-blue-800">Bottleneck Tasks</h4>
                                                            <div className="grid grid-cols-1 gap-3 mt-3">
                                                                {preboardingData.bottlenecks.slice(0, 3).map(task => (
                                                                    <div key={task.id} className="bg-card/60 rounded-lg p-3 flex justify-between items-center">
                                                                        <span className="font-medium text-foreground truncate flex-1">{task.title}</span>
                                                                        <span className="text-sm text-muted-foreground ml-4">{task.completionRate}% completion</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                        </div>
                                    )}

                                    {/* Onboarding Tab */}
                                    {activeTab === 'onboarding' && onboardingData && (
                                        <div className="space-y-6">
                                            <div className="bg-card rounded-xl border border-border p-6">
                                                <h3 className="font-semibold text-foreground mb-1">Avg Onboarding Duration</h3>
                                                <p className="text-3xl font-bold text-primary">
                                                    {onboardingData.averageOnboardingDurationDays != null
                                                        ? `${onboardingData.averageOnboardingDurationDays} days`
                                                        : '—'}
                                                </p>
                                            </div>

                                            {/* Employees who completed onboarding */}
                                            <div className="bg-card rounded-xl border border-border overflow-hidden">
                                                <div className="p-4 border-b border-border">
                                                    <h3 className="font-semibold text-foreground">Recently Completed</h3>
                                                </div>
                                                {onboardingData.onboardingCompletedList && onboardingData.onboardingCompletedList.length > 0 ? (
                                                    <DataTable
                                                        data={onboardingData.onboardingCompletedList}
                                                        columns={[
                                                            { key: 'name', label: 'Employee', render: (val) => <span className="font-medium text-foreground">{val}</span> },
                                                            { key: 'durationDays', label: 'Duration', render: (val) => val ? `${val} days` : '-' },
                                                            { key: 'onboardingCompleteDate', label: 'Completed', render: (val) => val ? new Date(val).toLocaleDateString() : '-' }
                                                        ]}
                                                        searchable
                                                        searchKeys={['name']}
                                                        pageSize={5}
                                                    />
                                                ) : (
                                                    <div className="p-8 text-center text-muted-foreground">
                                                        <p>No completions in this period</p>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Time to Complete */}
                                            <div className="bg-card rounded-xl border border-border overflow-hidden">
                                                <div className="p-4 border-b border-border">
                                                    <h3 className="font-semibold text-foreground">Time to Complete Onboarding</h3>
                                                    <p className="text-sm text-muted-foreground">Individual employee onboarding progress</p>
                                                </div>
                                                <DataTable
                                                    data={onboardingData.timeToComplete || []}
                                                    columns={[
                                                        { key: 'name', label: 'Employee', render: (val) => <span className="font-medium text-foreground">{val}</span> },
                                                        {
                                                            key: 'status', label: 'Status', render: (val) => (
                                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${val === 'active' ? 'bg-blue-100 text-blue-700' : 'bg-blue-100 text-blue-700'
                                                                    }`}>{val}</span>
                                                            )
                                                        },
                                                        {
                                                            key: 'onboarding', label: 'Onboarding', render: (val) => {
                                                                const data = val || { progress: 0, completed: 0, total: 0 };
                                                                return (
                                                                    <div className="flex flex-col gap-1 w-24">
                                                                        <div className="flex justify-between text-xs text-muted-foreground">
                                                                            <span>{data.progress}%</span>
                                                                            <span>{data.completed}/{data.total}</span>
                                                                        </div>
                                                                        <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                                                                            <div className="h-full bg-blue-600 rounded-full" style={{ width: `${data.progress}%` }} />
                                                                        </div>
                                                                    </div>
                                                                );
                                                            }
                                                        },
                                                        { key: 'daysToComplete', label: 'Days', render: (val) => val ? `${val} days` : '-' }
                                                    ]}
                                                    searchable
                                                    searchKeys={['name']}
                                                    pageSize={5}
                                                />
                                            </div>

                                            {/* Completion by Group */}
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                                {/* By Team */}
                                                <div className="bg-card rounded-xl border border-border p-6">
                                                    <h3 className="font-semibold text-foreground mb-4">Completion by Team</h3>
                                                    {onboardingData.byTeam && onboardingData.byTeam.length > 0 ? (
                                                        <BarChart
                                                            data={onboardingData.byTeam.slice(0, 5).map(t => ({
                                                                label: t.team_name,
                                                                value: t.completion_rate,
                                                                suffix: '%'
                                                            }))}
                                                            horizontal
                                                            barColor="#3B82F6"
                                                        />
                                                    ) : (
                                                        <p className="text-muted-foreground text-sm">No team data available</p>
                                                    )}
                                                </div>

                                                {/* By Department */}
                                                <div className="bg-card rounded-xl border border-border p-6">
                                                    <h3 className="font-semibold text-foreground mb-4">Completion by Department</h3>
                                                    {onboardingData.byDepartment && onboardingData.byDepartment.length > 0 ? (
                                                        <BarChart
                                                            data={onboardingData.byDepartment.slice(0, 5).map(d => ({
                                                                label: d.department,
                                                                value: d.completion_rate,
                                                                suffix: '%'
                                                            }))}
                                                            horizontal
                                                            barColor="#3B82F6"
                                                        />
                                                    ) : (
                                                        <p className="text-muted-foreground text-sm">No data available</p>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Bottleneck Tasks */}
                                            {onboardingData.bottlenecks && onboardingData.bottlenecks.length > 0 && (
                                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                                                    <div className="flex items-start gap-3">
                                                        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                                        <div className="flex-1">
                                                            <h4 className="font-semibold text-blue-800">Bottleneck Tasks</h4>
                                                            <p className="text-sm text-blue-700 mb-4">
                                                                These tasks are slowing down onboarding completion
                                                            </p>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                                {onboardingData.bottlenecks.slice(0, 4).map(task => (
                                                                    <div key={task.id} className="bg-card/60 rounded-lg p-3">
                                                                        <p className="font-medium text-foreground truncate">{task.title}</p>
                                                                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                                                                            <span>{task.completionRate}% completion</span>
                                                                            <span className={task.overdue > 0 ? 'text-red-600' : ''}>{task.overdue} overdue</span>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Employees Tab */}
                                    {activeTab === 'employees' && employeeData && (
                                        <div className="space-y-6">
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                                <div className="bg-red-50 rounded-xl border border-red-200 p-4">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <AlertTriangle className="w-4 h-4 text-red-500" />
                                                        <span className="text-xs font-semibold text-red-600">High Risk</span>
                                                    </div>
                                                    <p className="text-xl font-bold text-red-700">{employeeData.summary.highRisk}</p>
                                                </div>
                                                <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <AlertCircle className="w-4 h-4 text-blue-500" />
                                                        <span className="text-xs font-semibold text-blue-600">Medium Risk</span>
                                                    </div>
                                                    <p className="text-xl font-bold text-blue-700">{employeeData.summary.mediumRisk}</p>
                                                </div>
                                                <div className="bg-green-50 rounded-xl border border-green-200 p-4">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <CheckCircle className="w-4 h-4 text-blue-500" />
                                                        <span className="text-xs font-semibold text-blue-600">Low Risk</span>
                                                    </div>
                                                    <p className="text-xl font-bold text-blue-700">{employeeData.summary.lowRisk}</p>
                                                </div>
                                            </div>

                                            <div className="bg-card rounded-xl border border-border overflow-hidden">
                                                <div className="p-4 border-b border-border">
                                                    <h3 className="font-semibold text-foreground">Employee Performance</h3>
                                                </div>
                                                <DataTable
                                                    data={employeeData.employees}
                                                    columns={[
                                                        {
                                                            key: 'name', label: 'Name', render: (val, row) => (
                                                                <div className="flex flex-col">
                                                                    <span className="font-medium text-foreground">{val}</span>
                                                                    <span className="text-xs text-muted-foreground">{row.position}</span>
                                                                </div>
                                                            )
                                                        },
                                                        {
                                                            key: 'metrics.onboarding', label: 'Onboarding', render: (_, row) => {
                                                                const data = row.metrics?.onboarding || { progress: 0 };
                                                                return (
                                                                    <span className={`font-medium ${data.progress >= 80 ? 'text-blue-600' : 'text-blue-400'}`}>
                                                                        {data.progress}%
                                                                    </span>
                                                                );
                                                            }
                                                        },
                                                        {
                                                            key: 'metrics', label: 'Overdue', render: (val) => (
                                                                <span className={val.overdueTasks > 0 ? 'text-red-600 font-bold' : 'text-muted-foreground'}>
                                                                    {val.overdueTasks}
                                                                </span>
                                                            )
                                                        },
                                                        {
                                                            key: 'engagementScore', label: 'Engage', render: (val) => (
                                                                <span className={`font-medium ${val >= 70 ? 'text-blue-600' : val >= 40 ? 'text-blue-400' : 'text-red-600'}`}>
                                                                    {val}%
                                                                </span>
                                                            )
                                                        },
                                                        {
                                                            key: 'actions', label: '', render: (_, row) => (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setSelectedEmployeeId(row.id);
                                                                    }}
                                                                    className="text-primary hover:text-primary-hover"
                                                                >
                                                                    <ChevronRight className="w-4 h-4" />
                                                                </button>
                                                            )
                                                        }
                                                    ]}
                                                    onRowClick={(row) => setSelectedEmployeeId(row.id)}
                                                    searchable
                                                    searchKeys={['name', 'email']}
                                                    pageSize={10}
                                                />
                                            </div>

                                            {/* Reminder Summary Section */}
                                            {reminderSummary && reminderSummary.totalReminders > 0 && (
                                                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
                                                    <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
                                                        <h3 className="font-semibold text-gray-800 flex items-center gap-2 text-sm">
                                                            <div className="p-1.5 bg-blue-100 rounded-md">
                                                                <Bell className="w-4 h-4 text-blue-600" />
                                                            </div>
                                                            Reminder Activity
                                                        </h3>
                                                    </div>
                                                    <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-gray-100">
                                                        <div className="p-4 text-center">
                                                            <p className="text-2xl font-bold text-gray-800">{reminderSummary.totalReminders}</p>
                                                            <p className="text-xs font-medium text-gray-500 mt-1">Total Sent</p>
                                                        </div>
                                                        <div className="p-4 text-center">
                                                            <p className="text-2xl font-bold text-blue-600">{reminderSummary.automatedReminders}</p>
                                                            <div className="flex items-center justify-center gap-1.5 mt-1">
                                                                <Zap className="w-3 h-3 text-blue-500" />
                                                                <p className="text-xs font-medium text-gray-500">Automated</p>
                                                            </div>
                                                        </div>
                                                        <div className="p-4 text-center">
                                                            <p className="text-2xl font-bold text-indigo-600">{reminderSummary.manualReminders}</p>
                                                            <div className="flex items-center justify-center gap-1.5 mt-1">
                                                                <Send className="w-3 h-3 text-indigo-500" />
                                                                <p className="text-xs font-medium text-gray-500">Manual</p>
                                                            </div>
                                                        </div>
                                                        <div className="p-4 text-center bg-gray-50/50">
                                                            <p className={`text-2xl font-bold ${reminderSummary.overallEffectiveness >= 50 ? 'text-green-600' : reminderSummary.overallEffectiveness >= 25 ? 'text-amber-600' : 'text-red-600'}`}>
                                                                {reminderSummary.overallEffectiveness}%
                                                            </p>
                                                            <p className="text-xs font-medium text-gray-500 mt-1">Effectiveness</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Reminder History */}
                                            <ReminderHistory />

                                            {/* AI Chat Panel */}
                                            <AnalyticsAIPanel
                                                analyticsContext={{
                                                    type: 'employees',
                                                    title: 'Employee Analytics',
                                                    data: employeeData,
                                                }}
                                            />

                                            {/* Notification Settings */}
                                            <NotificationSettingsPanel
                                                analyticsContext={{
                                                    type: 'employees',
                                                    title: 'Employee Analytics',
                                                    data: employeeData,
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* Employee Detail Modal */}
                {
                    selectedEmployeeId && (
                        <EmployeeDetailModal
                            employeeId={selectedEmployeeId}
                            onClose={() => setSelectedEmployeeId(null)}
                        />
                    )
                }

            </div>
        </div>
    );
};

export default EmployeesAnalyticsDrawer;
